/**
 * Голосовые сообщения: запись с микрофона (MediaRecorder) с волной по ходу записи, загрузка
 * файлом и отправка с флагом IS_VOICE_MESSAGE. Формат — какой умеет браузер: Firefox пишет
 * Ogg/Opus, Chrome и приложение — WebM/Opus, Safari — MP4/AAC; современные браузеры играют все три.
 */
import { socialApi } from './socialApi'
import { uploadFile } from './upload'
import type { AttachmentRef } from '@/types/api'

export const MAX_VOICE_SECONDS = 20 * 60
const WAVEFORM_POINTS = 256
const SAMPLE_MS = 50

const FORMATS: { mime: string; type: string; ext: string }[] = [
  { mime: 'audio/ogg;codecs=opus', type: 'audio/ogg', ext: 'ogg' },
  { mime: 'audio/webm;codecs=opus', type: 'audio/webm', ext: 'webm' },
  { mime: 'audio/mp4', type: 'audio/mp4', ext: 'm4a' },
  { mime: 'audio/webm', type: 'audio/webm', ext: 'webm' },
]

export const canRecordVoice = typeof window !== 'undefined' && typeof MediaRecorder !== 'undefined' && Boolean(navigator.mediaDevices?.getUserMedia)

function pickFormat() {
  return FORMATS.find((f) => MediaRecorder.isTypeSupported(f.mime)) ?? null
}

export interface VoiceClip {
  blob: Blob
  type: string
  ext: string
  seconds: number
  /** Громкость по ходу записи, 0…1, каждые 50 мс. */
  levels: number[]
}

/** Идущая запись: уровни для живой волны, остановка с готовым файлом и отмена. */
export interface VoiceRecording {
  levels: () => number[]
  elapsed: () => number
  stop: () => Promise<VoiceClip>
  cancel: () => void
}

export async function startVoiceRecording(constraints: MediaTrackConstraints): Promise<VoiceRecording> {
  const format = pickFormat()
  if (!format) throw new Error('Этот браузер не умеет записывать звук')
  const stream = await navigator.mediaDevices.getUserMedia({ audio: constraints })
  const recorder = new MediaRecorder(stream, { mimeType: format.mime, audioBitsPerSecond: 48000 })
  const chunks: Blob[] = []
  recorder.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data) }

  // уровень громкости для волны: RMS окна анализатора раз в 50 мс
  const ctx = new AudioContext()
  const analyser = ctx.createAnalyser()
  analyser.fftSize = 1024
  ctx.createMediaStreamSource(stream).connect(analyser)
  const frame = new Float32Array(analyser.fftSize)
  const levels: number[] = []
  const tick = setInterval(() => {
    analyser.getFloatTimeDomainData(frame)
    let sum = 0
    for (const v of frame) sum += v * v
    levels.push(Math.min(1, Math.sqrt(sum / frame.length) * 4))
  }, SAMPLE_MS)

  const started = performance.now()
  const release = () => {
    clearInterval(tick)
    for (const t of stream.getTracks()) t.stop()
    void ctx.close().catch(() => undefined)
  }
  recorder.start(250)
  return {
    levels: () => levels,
    elapsed: () => (performance.now() - started) / 1000,
    stop: () => new Promise<VoiceClip>((resolve, reject) => {
      const seconds = (performance.now() - started) / 1000
      recorder.onstop = () => {
        release()
        resolve({ blob: new Blob(chunks, { type: format.type }), type: format.type, ext: format.ext, seconds, levels: [...levels] })
      }
      recorder.onerror = () => {
        release()
        reject(new Error('Запись прервалась'))
      }
      recorder.stop()
    }),
    cancel: () => {
      recorder.onstop = null
      if (recorder.state !== 'inactive') recorder.stop()
      release()
    },
  }
}

/** Волна для сервера: до 256 значений 0–255 в base64; громкость растянута до самого громкого места. */
export function encodeWaveform(levels: number[]): string {
  const n = Math.max(1, Math.min(WAVEFORM_POINTS, levels.length))
  const top = Math.max(0.05, ...levels)
  const bytes = new Uint8Array(n)
  for (let i = 0; i < n; i++) {
    const from = Math.floor((i * levels.length) / n)
    const to = Math.max(from + 1, Math.floor(((i + 1) * levels.length) / n))
    let peak = 0
    for (let j = from; j < to && j < levels.length; j++) peak = Math.max(peak, levels[j]!)
    bytes[i] = Math.round(Math.min(1, peak / top) * 255)
  }
  return btoa(String.fromCharCode(...bytes))
}

/** Волна из base64 → значения 0…1 для рисования. */
export function decodeWaveform(b64: string | undefined): number[] {
  if (!b64) return []
  try {
    return [...atob(b64)].map((c) => c.charCodeAt(0) / 255)
  } catch {
    return []
  }
}

/** Загрузить запись и вернуть ссылку на вложение с длительностью и волной. */
export async function uploadVoice(channelId: string, clip: VoiceClip): Promise<AttachmentRef> {
  const name = `voice-message.${clip.ext}`
  const file = new File([clip.blob], name, { type: clip.type })
  const { attachments } = await socialApi.createUploads(channelId, [{ id: '0', filename: name, file_size: file.size, content_type: clip.type }])
  const slot = attachments[0]
  if (!slot) throw new Error('Хранилище не дало ссылку на загрузку')
  await uploadFile(slot.upload_url, file, () => undefined)
  return { id: '0', filename: name, uploaded_filename: slot.upload_filename, duration_secs: Math.round(clip.seconds * 100) / 100, waveform: encodeWaveform(clip.levels) }
}

/** «1:07» из секунд. */
export function clock(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds))
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}
